import { useState, useEffect, useCallback, useMemo } from 'react';
import { TodoItem } from '../../types/todos';
import { todosApi } from '../../api/todosApi';

interface UseTodosProps {
    userId?: string;
    buildingKey?: string;
}

interface UseTodosReturn {
    todos: { [key: string]: TodoItem };
    loading: boolean;
    error: string | null;
    submitting: boolean;
    activeTodos: TodoItem[];
    completedTodos: TodoItem[];
    addTodo: (text: string) => Promise<void>;
    toggleTodo: (todoKey: string, currentTodo: TodoItem) => Promise<void>;
    updateTodo: (todoKey: string, currentTodo: TodoItem, newText: string) => Promise<void>;
    deleteTodo: (todoKey: string) => Promise<void>;
    clearError: () => void;
}

export const useTodos = ({ userId, buildingKey }: UseTodosProps): UseTodosReturn => {
    const [todos, setTodos] = useState<{ [key: string]: TodoItem }>({});
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [submitting, setSubmitting] = useState(false);

    // Determine context (building or user)
    const isUserContext = !!userId;
    const contextKey = isUserContext ? userId : buildingKey;

    // Memoize active and completed todos
    const activeTodos = useMemo(() =>
        Object.values(todos).filter(todo => !todo.done),
        [todos]
    );

    const completedTodos = useMemo(() =>
        Object.values(todos).filter(todo => todo.done),
        [todos]
    );

    // Load todos
    const loadTodos = useCallback(async () => {
        if (!contextKey) return;

        setLoading(true);
        setError(null);

        try {
            const loadedTodos = await todosApi.getTodos(contextKey, isUserContext);
            setTodos(loadedTodos);
        } catch (err) {
            console.error('Error loading todos:', err);
            setError('Kunde inte ladda att göra-listan');
        } finally {
            setLoading(false);
        }
    }, [contextKey, isUserContext]);

    // Load todos on mount or when context changes
    useEffect(() => {
        if (contextKey) {
            loadTodos();
        }
    }, [contextKey, loadTodos]);

    // Add new todo
    const addTodo = useCallback(async (text: string) => {
        if (!contextKey) {
            setError('Kontextfel: ingen byggnad eller användare vald');
            return;
        }

        setSubmitting(true);
        setError(null);

        try {
            await todosApi.createTodo(contextKey, text, isUserContext);
            await loadTodos();
        } catch (err: any) {
            console.error('Error adding todo:', err);
            setError(err.message || 'Kunde inte lägga till uppgift');
            throw err;
        } finally {
            setSubmitting(false);
        }
    }, [contextKey, isUserContext, loadTodos]);

    // Toggle todo done status
    const toggleTodo = useCallback(async (todoKey: string, currentTodo: TodoItem) => {
        if (!contextKey) return;

        try {
            await todosApi.toggleTodo(contextKey, todoKey, currentTodo, isUserContext);
            await loadTodos();
        } catch (err) {
            console.error('Error toggling todo:', err);
            setError('Kunde inte uppdatera uppgift');
        }
    }, [contextKey, isUserContext, loadTodos]);

    // Update todo text
    const updateTodo = useCallback(async (todoKey: string, currentTodo: TodoItem, newText: string) => {
        if (!contextKey) return;

        setSubmitting(true);
        setError(null);

        try {
            await todosApi.updateTodo(contextKey, todoKey, currentTodo, newText, isUserContext);
            await loadTodos();
        } catch (err: any) {
            console.error('Error updating todo:', err);
            setError(err.message || 'Kunde inte uppdatera uppgift');
            throw err;
        } finally {
            setSubmitting(false);
        }
    }, [contextKey, isUserContext, loadTodos]);

    // Delete todo
    const deleteTodo = useCallback(async (todoKey: string) => {
        if (!contextKey) return;

        try {
            await todosApi.deleteTodo(contextKey, todoKey, isUserContext);
            await loadTodos();
        } catch (err) {
            console.error('Error deleting todo:', err);
            setError('Kunde inte radera uppgift');
        }
    }, [contextKey, isUserContext, loadTodos]);

    // Clear error
    const clearError = useCallback(() => {
        setError(null);
    }, []);

    return {
        todos,
        loading,
        error,
        submitting,
        activeTodos,
        completedTodos,
        addTodo,
        toggleTodo,
        updateTodo,
        deleteTodo,
        clearError,
    };
};
